import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './car-or-van-availability.reducer';

export const CarOrVanAvailability = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const carOrVanAvailabilityList = useAppSelector(state => state.carOrVanAvailability.entities);
  const loading = useAppSelector(state => state.carOrVanAvailability.loading);
  const totalItems = useAppSelector(state => state.carOrVanAvailability.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="car-or-van-availability-heading" data-cy="CarOrVanAvailabilityHeading">
        <Translate contentKey="apiLocationApp.carOrVanAvailability.home.title">Car Or Van Availabilities</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="apiLocationApp.carOrVanAvailability.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/car-or-van-availability/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="apiLocationApp.carOrVanAvailability.home.createLabel">Create new Car Or Van Availability</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {carOrVanAvailabilityList && carOrVanAvailabilityList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="apiLocationApp.carOrVanAvailability.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('geographicLevel')}>
                  <Translate contentKey="apiLocationApp.carOrVanAvailability.geographicLevel">Geographic Level</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographicLevel')} />
                </th>
                <th className="hand" onClick={sort('date')}>
                  <Translate contentKey="apiLocationApp.carOrVanAvailability.date">Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('date')} />
                </th>
                <th className="hand" onClick={sort('geography')}>
                  <Translate contentKey="apiLocationApp.carOrVanAvailability.geography">Geography</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geography')} />
                </th>
                <th className="hand" onClick={sort('geographyCode')}>
                  <Translate contentKey="apiLocationApp.carOrVanAvailability.geographyCode">Geography Code</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographyCode')} />
                </th>
                <th className="hand" onClick={sort('totalAllHouseholds')}>
                  <Translate contentKey="apiLocationApp.carOrVanAvailability.totalAllHouseholds">Total All Households</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('totalAllHouseholds')} />
                </th>
                <th className="hand" onClick={sort('noCarsVans')}>
                  <Translate contentKey="apiLocationApp.carOrVanAvailability.noCarsVans">No Cars Vans</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('noCarsVans')} />
                </th>
                <th className="hand" onClick={sort('oneCar')}>
                  <Translate contentKey="apiLocationApp.carOrVanAvailability.oneCar">One Car</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('oneCar')} />
                </th>
                <th className="hand" onClick={sort('twoCars')}>
                  <Translate contentKey="apiLocationApp.carOrVanAvailability.twoCars">Two Cars</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('twoCars')} />
                </th>
                <th className="hand" onClick={sort('threeOrMoreCars')}>
                  <Translate contentKey="apiLocationApp.carOrVanAvailability.threeOrMoreCars">Three Or More Cars</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('threeOrMoreCars')} />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {carOrVanAvailabilityList.map((carOrVanAvailability, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/car-or-van-availability/${carOrVanAvailability.id}`} color="link" size="sm">
                      {carOrVanAvailability.id}
                    </Button>
                  </td>
                  <td>
                    <Translate contentKey={`apiLocationApp.GeographicLevel.${carOrVanAvailability.geographicLevel}`} />
                  </td>
                  <td>{carOrVanAvailability.date}</td>
                  <td>{carOrVanAvailability.geography}</td>
                  <td>{carOrVanAvailability.geographyCode}</td>
                  <td>{carOrVanAvailability.totalAllHouseholds}</td>
                  <td>{carOrVanAvailability.noCarsVans}</td>
                  <td>{carOrVanAvailability.oneCar}</td>
                  <td>{carOrVanAvailability.twoCars}</td>
                  <td>{carOrVanAvailability.threeOrMoreCars}</td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/car-or-van-availability/${carOrVanAvailability.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/car-or-van-availability/${carOrVanAvailability.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/car-or-van-availability/${carOrVanAvailability.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="apiLocationApp.carOrVanAvailability.home.notFound">No Car Or Van Availabilities found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={carOrVanAvailabilityList && carOrVanAvailabilityList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default CarOrVanAvailability;
